import * as Plot from '@observablehq/plot';
import { max, repeat, sum } from 'lodash-es';
import { ChartTooltip } from '../../../components/Chart/components/ChartTooltip';
import { generateTimeTicks } from '../timeTicksUtils';
import { pointer } from '../TooltipMark/pointer';
import { tooltip } from '../TooltipMark/tooltipMark';
import { formatValue, getTextWidth, toTitleCase } from '../utils';
import {
	AXIS_TITLE_TO_TICK_LABEL_PADDING,
	COMMON_PADDING,
	SCATTER_CHART_DOT_RADIUS,
	TICK_LABEL_TO_AXIS_PADDING,
} from './constants';
import { TooltipContentRenderer } from './TooltipContentRenderer';
import type { ChartRenderFunction, LegendsInfo } from './types';

export const renderScatterChart: ChartRenderFunction = ({
	results,
	chartConfig: _chartConfig,
	containerRef,
	chartWidth,
	chartHeight,
	theme,
	chartConfigHelper: chartConfigHelper,
}) => {
	const maxRadius = Math.min(chartWidth, chartHeight) / 40;
	const minRadius = Math.min(chartWidth, chartHeight) / 120;
	const chartConfig = chartConfigHelper.getAsScatterChartConfig();
	const scatterAxisConfig = chartConfig.axes;
	const xAxisColumn = scatterAxisConfig.x[0];
	const yAxisColumn = scatterAxisConfig.y[0];
	const sizeColumn = scatterAxisConfig.size[0];
	const fontFamily = theme.fontFamily || 'inherit';
	const {
		colors,
		showGrid,
		xIsTime,
		fontSize,
		lineHeight,
		shouldShowXAxisTitle,
		shouldShowYAxisTitle,
		xAxisTitle,
		yAxisTitle,
	} = chartConfigHelper;

	const xValues = results.map((result) => result[xAxisColumn]);

	let { xAxisTimeInterval } = chartConfigHelper;

	const xTickFormat = (d: any) => {
		const columnFormat = chartConfig.columnConfigs[xAxisColumn];
		return formatValue(d, columnFormat, xAxisTimeInterval);
	};

	const yTickFormat = (d: any) => {
		const columnFormat = chartConfig.columnConfigs[yAxisColumn];
		return formatValue(d, columnFormat);
	};

	let xTicksDate: Date[] = [];
	const { forcexAxisRotation } = chartConfigHelper;
	let xAxisRotation = chartConfigHelper.xAxisTickRotation;

	if (xIsTime) {
		const r = generateTimeTicks({
			// Subtract margin
			chartWidth: chartWidth - 64,
			tickFontSize: fontSize,
			timeValues: xValues,
			interval: xAxisTimeInterval,
			xTickFormat,
		});
		xTicksDate = r.ticks;
		if (xAxisTimeInterval === 'auto') {
			xAxisTimeInterval = r.suggestedInterval;
		}

		if (!forcexAxisRotation) {
			xAxisRotation = r.suggestedRotation;
		}
	}

	if (
		!forcexAxisRotation &&
		sum(
			results.map((d) =>
				getTextWidth(
					xTickFormat(d[xAxisColumn]) + repeat(' ', 5),
					fontFamily,
					fontSize
				)
			)
		) >
			chartWidth - 64
	) {
		xAxisRotation = 45;
	}

	const marks: Plot.Markish[] = [];

	let xTicksTextWidth =
		max(
			results.map((d) =>
				getTextWidth(xTickFormat(d[xAxisColumn]), fontFamily, fontSize)
			)
		) || 0;

	let xTicksTextHeight = lineHeight;
	if (xAxisRotation >= 45) {
		xTicksTextHeight =
			xTicksTextWidth * Math.sin((xAxisRotation * Math.PI) / 180) +
			lineHeight * Math.sin((xAxisRotation * Math.PI) / 180);
	} else if (xAxisRotation === 0) {
		xTicksTextHeight = lineHeight;
	}

	let yTicksTextWidth =
		max(
			results.map((d) =>
				getTextWidth(yTickFormat(d[yAxisColumn]) + '.5', fontFamily, fontSize)
			)
		) || 0;

	const xAxisTitleProperties = {
		label: shouldShowXAxisTitle ? xAxisTitle : null,
		labelOffset: shouldShowXAxisTitle
			? TICK_LABEL_TO_AXIS_PADDING +
				xTicksTextHeight +
				AXIS_TITLE_TO_TICK_LABEL_PADDING
			: undefined,
	};

	const yAxisTitleProperties = {
		label: shouldShowYAxisTitle ? yAxisTitle : null,
		labelOffset: shouldShowYAxisTitle
			? TICK_LABEL_TO_AXIS_PADDING +
				yTicksTextWidth +
				AXIS_TITLE_TO_TICK_LABEL_PADDING
			: undefined,
	};

	const legends: LegendsInfo = {};
	let index = 0;
	const resultsWithCategory: Record<string, any>[] = results.map((result) => {
		let category: string = yAxisColumn;
		if (scatterAxisConfig?.category?.length > 0) {
			category = `${yAxisColumn} | ${scatterAxisConfig?.category?.map((c) => result[c]).join(' | ')}`;
		}

		if (!legends[category]) {
			let shortDisplayValue = toTitleCase(yAxisColumn);
			if (scatterAxisConfig?.category?.length > 0) {
				shortDisplayValue = toTitleCase(
					scatterAxisConfig?.category?.map((c) => result[c]).join(' | ')
				);
			}
			legends[category] = {
				color: colors[index % colors.length],
				shortDisplayValue,
			};
			index += 1;
		}

		return {
			...result,
			_category: category,
		};
	});

	const getColor = (d: Record<string, any>) => legends[d._category].color;

	// Add grid lines if enabled
	if (showGrid) {
		marks.push(
			Plot.gridY({
				stroke: theme.other.getColor('border/secondary/default'),
				strokeOpacity: 1.0,
			}),
			Plot.gridX({
				stroke: theme.other.getColor('border/secondary/default'),
				strokeOpacity: 1.0,
			})
		);
	}

	marks.push(
		Plot.axisX({
			tickSize: 0,
			tickFormat: xTickFormat,
			fontSize: fontSize,
			color: theme.other.getColor('text/secondary/default'),
			labelArrow: 'none',
			labelAnchor: 'center',
			tickPadding:
				TICK_LABEL_TO_AXIS_PADDING +
				(xAxisRotation === 0 ? 0 : xTicksTextHeight / 2),
			...xAxisTitleProperties,
			ticks: xIsTime ? xTicksDate : undefined,
			rotate: -xAxisRotation,
		}),
		Plot.axisY({
			tickFormat: yTickFormat,
			fontSize: fontSize,
			tickSize: 0,
			...yAxisTitleProperties,
			labelArrow: 'none',
			tickPadding: TICK_LABEL_TO_AXIS_PADDING,
			labelAnchor: 'center',
			color: theme.other.getColor('text/secondary/default'),
		})
	);

	const maxSize = sizeColumn
		? Math.max(...results.map((result) => result[sizeColumn]))
		: 0;

	const minSize = sizeColumn
		? Math.min(...results.map((result) => result[sizeColumn]))
		: 0;

	const getSize = (d: Record<string, any>) => {
		if (sizeColumn) {
			const size = d[sizeColumn] || 0;
			if (minSize === maxSize) {
				return minRadius;
			}

			return (
				((size - minSize) / (maxSize - minSize)) * (maxRadius - minRadius) +
				minRadius
			);
		}
		return 0;
	};

	marks.push(
		Plot.dot(resultsWithCategory, {
			x: xAxisColumn,
			y: yAxisColumn,
			r: SCATTER_CHART_DOT_RADIUS,
			fill: getColor,
			fillOpacity: 0.5,
			stroke: getColor,
			strokeWidth: 1,
		}),
		Plot.dot(
			resultsWithCategory,
			pointer({
				x: xAxisColumn,
				y: yAxisColumn,
				r: sizeColumn
					? (d: Record<string, any>) => getSize(d) * 1.5
					: SCATTER_CHART_DOT_RADIUS,
				fill: getColor,
				fillOpacity: 0.5,
				stroke: getColor,
				strokeWidth: 1,
			})
		),
		tooltip(
			resultsWithCategory,
			pointer({
				x: xAxisColumn,
				y: yAxisColumn,
				tooltipRenderer: ({
					x,
					y,
					dataIndex,
				}: {
					x: number;
					y: number;
					dataIndex: number;
				}) => {
					const data = resultsWithCategory[dataIndex];
					const columnAndValuePairs = [
						{
							columnFormattedName: xAxisTitle,
							formattedValue: xTickFormat(data[xAxisColumn]),
						},
						{
							columnFormattedName: yAxisTitle,
							formattedValue: yTickFormat(data[yAxisColumn]),
						},
					];
					if (sizeColumn) {
						columnAndValuePairs.push({
							columnFormattedName: toTitleCase(sizeColumn),
							formattedValue: formatValue(
								data[sizeColumn],
								chartConfig.columnConfigs[sizeColumn]
							),
						});
					}
					return (
						<ChartTooltip x={x + 12} y={y}>
							<TooltipContentRenderer
								columnAndValuePairs={columnAndValuePairs}
							/>
						</ChartTooltip>
					);
				},
			})
		)
	);

	marks.push(
		Plot.frame({
			stroke: theme.other.getColor('border/secondary/default'),
		})
	);

	const plot = Plot.plot({
		marks,
		width: chartWidth,
		height: chartHeight,
		marginLeft:
			TICK_LABEL_TO_AXIS_PADDING +
			yTicksTextWidth +
			(shouldShowYAxisTitle ? AXIS_TITLE_TO_TICK_LABEL_PADDING : 0) +
			COMMON_PADDING,
		marginBottom:
			TICK_LABEL_TO_AXIS_PADDING +
			xTicksTextHeight +
			(shouldShowXAxisTitle ? AXIS_TITLE_TO_TICK_LABEL_PADDING : 0) +
			COMMON_PADDING,
		marginRight: COMMON_PADDING * 2,
		marginTop: COMMON_PADDING * 2,
		inset: COMMON_PADDING * 2,
		color: { legend: false },
		x: {
			nice: true,
		},
		y: {
			nice: true,
		},
		r: {
			type: 'identity',
		},
		style: {
			background: 'transparent',
			fontFamily: theme.fontFamily,
			fontSize: `${fontSize}px`,
		},
	});

	containerRef.current?.appendChild(plot);
	return {
		plot,
		legends,
	};
};
